import React, { useState } from "react";
import toast from "react-hot-toast";
import axios from "axios";
import { authApiService } from "../../../api/api";
import { TerminalLockerLiveItem } from "../../../types/terminal-locker-live.type";
import styles from "../TerminalLiveCard/styles.module.scss";

function formatBleAt(iso: string | null | undefined): string {
  if (!iso) {
    return "—";
  }
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
}

function formatApiError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as
      | { message?: string | string[] }
      | undefined;
    const m = data?.message;
    if (Array.isArray(m)) {
      return m.join(", ");
    }
    if (typeof m === "string") {
      return m;
    }
  }
  return "Не вдалося виконати команду.";
}

export interface TerminalBleDevicesModalProps {
  terminal: TerminalLockerLiveItem;
  isOpen: boolean;
  onClose: () => void;
  onMetaSaved?: () => void | Promise<void>;
}

const TerminalBleDevicesModal: React.FC<TerminalBleDevicesModalProps> = ({
  terminal,
  isOpen,
  onClose,
  onMetaSaved,
}) => {
  const [isBleRefreshPending, setIsBleRefreshPending] = useState(false);
  const [isClearingQueue, setIsClearingQueue] = useState(false);

  if (!isOpen) {
    return null;
  }

  const busy = isBleRefreshPending || isClearingQueue;

  const handleRequestBleScan = async () => {
    setIsBleRefreshPending(true);
    try {
      const res = await authApiService.requestTerminalBleScan(terminal.id);
      if (!res) {
        toast.error("Потрібна авторизація. Увійдіть знову.");
        return;
      }
      const pushOk = (res as { bleWsPushSent?: boolean }).bleWsPushSent;
      if (pushOk === false) {
        toast.success(
          "Прапор збережено. Зараз немає WebSocket до терміналу — після з’єднання з CRM команда ble_scan піде автоматично. Переконайтесь, що на одному хості bike-crm (або session affinity для WS).",
          { duration: 8000 }
        );
      } else if (pushOk === true) {
        toast.success("Команда ble_scan надіслана на термінал по WebSocket.");
      } else {
        toast.success(
          "Прапор збережено. Після появи сокета скан зникне з черги. Оновіть сторінку після зміни даних."
        );
      }
      await onMetaSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setIsBleRefreshPending(false);
    }
  };

  const handleClearBleQueue = async () => {
    setIsClearingQueue(true);
    try {
      const res = await authApiService.clearTerminalBleScanRequest(terminal.id);
      if (!res) {
        toast.error("Потрібна авторизація. Увійдіть знову.");
        return;
      }
      toast.success("Чергу BLE скану скинуто.");
      await onMetaSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setIsClearingQueue(false);
    }
  };

  return (
    <div
      className={styles.confirmBackdrop}
      onClick={() => !busy && onClose()}
      role="presentation"
    >
      <div
        className={`${styles.confirmModal} ${styles.bleModal}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ble-devices-title"
      >
        <h3 id="ble-devices-title" className={styles.confirmTitle}>
          BLE пристрої
        </h3>
        <p className={styles.bleSub}>
          Термінал: <strong>{terminal.terminal_id}</strong>
        </p>
        {terminal.bleScanRequested && (
          <p className={styles.blePendingNote}>
            Очікується скан (команда в черзі)…
            <button
              type="button"
              className={styles.bleClearQueue}
              onClick={handleClearBleQueue}
              disabled={busy}
            >
              {isClearingQueue ? "Скидання…" : "Скинути чергу"}
            </button>
          </p>
        )}
        <p className={styles.bleRow}>
          <span className={styles.bleLabel}>Останнє оновлення</span>
          <span className={styles.bleValue}>
            {formatBleAt(terminal.bleLastUpdatedAt)}
          </span>
        </p>
        <ul className={styles.bleList}>
          {(terminal.bleDevices ?? []).length === 0 ? (
            <li className={styles.bleListEmpty}>Ще немає збереженого скану.</li>
          ) : (
            (terminal.bleDevices ?? []).map((d) => (
              <li key={d.address} className={styles.bleListItem}>
                <code className={styles.bleAddr}>{d.address}</code>
                <span className={styles.bleName}>
                  {d.name && d.name.trim() !== "" ? d.name : "—"}
                </span>
              </li>
            ))
          )}
        </ul>
        <div className={styles.bleActions}>
          <button
            type="button"
            className={styles.confirmButtonSecondary}
            onClick={onClose}
            disabled={busy}
          >
            Закрити
          </button>
          <button
            type="button"
            className={styles.confirmButtonPrimary}
            onClick={handleRequestBleScan}
            disabled={busy}
          >
            {isBleRefreshPending ? "Надсилання…" : "Оновити"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TerminalBleDevicesModal;
